import { DeleteIcon } from "@chakra-ui/icons";
import { Box, Stack, Text } from "@chakra-ui/layout";
import { useToast } from "@chakra-ui/toast";
import axios from "axios";
import { useEffect, useState } from "react";
import {
  decodeStoredContent,
  formatUnreadCount,
  getMessageSenderId,
  getSender,
  getSenderFull,
  isMessageSeenByOthers,
} from "../config/ChatLogics";
import ChatLoading from "./ChatLoading";
import GroupChatModal from "./miscellaneous/GroupChatModal";
import GroupChatAvatar from "./userAvatar/GroupChatAvatar";
import { Avatar, Button, Input, IconButton } from "@chakra-ui/react";
import { ChatState } from "../Context/ChatProvider";

const CHATS_PAGE_SIZE = 20;

const MyChats = ({ fetchAgain }) => {
  const [loggedUser, setLoggedUser] = useState();
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filter, setFilter] = useState("");
  const [deletingId, setDeletingId] = useState(null);

  const {
    selectedChat,
    setSelectedChat,
    user,
    chats,
    setChats,
    presenceMap,
    setPresenceMap,
  } = ChatState();

  const toast = useToast();

  const normalizeChats = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.chats)) return data.chats;
    return [];
  };

  const fetchChats = async (pageNum = 1, append = false) => {
    if (!user?.token) return;
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      };

      if (append) setLoadingMore(true);
      const { data, headers } = await axios.get(
        `/api/chat?page=${pageNum}&limit=${CHATS_PAGE_SIZE}${
          filter.trim()
            ? `&search=${encodeURIComponent(filter.trim())}`
            : ""
        }`,
        config
      );
      const list = normalizeChats(data);
      const headerHasMore = String(headers?.["x-has-more"] || "").toLowerCase() === "true";
      const more = headers?.["x-has-more"] !== undefined
        ? headerHasMore
        : list.length === CHATS_PAGE_SIZE;

      if (append) {
        setChats((prev) => {
          const prevList = Array.isArray(prev) ? prev : [];
          const seen = new Set(prevList.map((c) => String(c._id)));
          const merged = [...prevList];
          list.forEach((c) => {
            if (!seen.has(String(c._id))) merged.push(c);
          });
          return merged;
        });
      } else {
        setChats(list);
      }
      setPage(pageNum);
      setHasMore(more);
      if (append) setLoadingMore(false);

      // Bulk presence for 1-1 peers.
      const peerIds = list
        .filter((c) => !c.isGroupChat)
        .map((c) => {
          const peer = getSenderFull(user, c.users);
          return peer?._id ? String(peer._id) : null;
        })
        .filter(Boolean)
        .slice(0, 50);
      if (peerIds.length > 0 && typeof setPresenceMap === "function") {
        try {
          const { data: pres } = await axios.get(
            `/api/user/presence?ids=${peerIds.join(",")}`,
            config
          );
          if (Array.isArray(pres)) {
            setPresenceMap((prev) => {
              const next = { ...(prev || {}) };
              pres.forEach((p) => {
                next[String(p._id)] = {
                  isOnline: !!p.isOnline,
                  lastSeen: p.lastSeen,
                };
              });
              return next;
            });
          }
        } catch {
          // presence is best-effort
        }
      }
    } catch (error) {
      if (append) setLoadingMore(false);
      toast({
        title: "Error Occured!",
        description: "Failed to Load the chats",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom-left",
      });
    }
  };

  useEffect(() => {
    try {
      setLoggedUser(JSON.parse(localStorage.getItem("userInfo")));
    } catch {
      setLoggedUser(null);
    }
    setPage(1);
    fetchChats(1, false);
    // eslint-disable-next-line
  }, [fetchAgain, user?._id]);

  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      fetchChats(1, false);
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [filter]);

  const handleDelete = async (e, chat) => {
    e.stopPropagation();
    const name = chat.isGroupChat ? chat.chatName : getSender(loggedUser, chat.users);
    const msg = chat.isGroupChat
      ? `Delete group "${name}"? This removes it and all messages for everyone.`
      : `Delete "${name}"? This deletes the chat and all its messages for you only — the other person keeps everything.`;
    if (!window.confirm(msg)) return;
    try {
      setDeletingId(chat._id);
      const config = {
        headers: { Authorization: `Bearer ${user?.token}` },
      };
      await axios.delete(`/api/chat/${chat._id}`, config);
      if (typeof setChats === "function") {
        setChats((prev) =>
          Array.isArray(prev) ? prev.filter((c) => String(c._id) !== String(chat._id)) : prev
        );
      }
      if (selectedChat && String(selectedChat._id) === String(chat._id)) {
        setSelectedChat(null);
      }
      toast({
        title: "Chat deleted",
        status: "success",
        duration: 2500,
        isClosable: true,
        position: "bottom-left",
      });
    } catch (error) {
      toast({
        title: "Failed to delete chat",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom-left",
      });
    } finally {
      setDeletingId(null);
    }
  };

  const canDelete = (chat) => {
    if (!chat.isGroupChat) return true;
    const adminId = chat.groupAdmin?._id || chat.groupAdmin;
    return String(adminId) === String(user?._id);
  };

  return (
    <Box
      display={{ base: selectedChat ? "none" : "flex", md: "flex" }}
      flexDir="column"
      alignItems="center"
      p={3}
      bg="var(--ch-panel)"
      borderColor="var(--ch-panel-border)"
      backdropFilter="blur(18px)"
      color="var(--ch-text)"
      w={{ base: "100%", md: "31%" }}
      borderRadius="xl"
      borderWidth="1px"
    >
      <Box
        pb={2}
        px={3}
        display="flex"
        w="100%"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
      >
        <Input
          size="sm"
          className="chat-input"
          placeholder="Search chats…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          minW={0}
          flex="1"
        />
        <GroupChatModal>
          <Button
            display="flex"
            fontSize={{ base: "17px", md: "10px", lg: "17px" }}
            className="chat-btn-primary"
            flexShrink={0}
          >
            New Group
          </Button>
        </GroupChatModal>
      </Box>
      <Box
        display="flex"
        flexDir="column"
        p={3}
        bg="var(--ch-searchpanel)"
        border="1px solid var(--ch-item-border)"
        w="100%"
        h="100%"
        borderRadius="lg"
        overflowY="hidden"
      >
        {chats ? (
          <>
            <Stack overflowY="auto" className="chatlist-scroll">
              {chats.length === 0 && (
                <Box textAlign="center" py={8}>
                  <Text fontWeight="700" color="var(--ch-text)">
                    {filter.trim()
                      ? `No chats match "${filter.trim()}"`
                      : "No conversations yet"}
                  </Text>
                  <Text fontSize="xs" color="var(--ch-faint)" mt={1}>
                    {filter.trim()
                      ? "Try a different filter."
                      : "Search users above to start chatting."}
                  </Text>
                </Box>
              )}
              {chats.map((chat) => {
                const myId = loggedUser?._id || user?._id;
                const unreadLabel = formatUnreadCount(chat.unreadCount);
                const senderId = getMessageSenderId(chat.latestMessage);
                const isMine =
                  !!senderId && !!myId && senderId === String(myId);
                const seen =
                  !!chat.latestMessage &&
                  isMine &&
                  isMessageSeenByOthers(
                    chat.latestMessage,
                    chat.users,
                    myId
                  );
                const peer = !chat.isGroupChat
                  ? getSenderFull(loggedUser || user, chat.users)
                  : null;
                const peerPresence = peer?._id
                  ? presenceMap?.[String(peer._id)]
                  : null;
                const isOnline = peer ? !!peerPresence?.isOnline : false;
                return (
                  <Box
                    onClick={() => setSelectedChat(chat)}
                    cursor="pointer"
                    bg={
                      selectedChat?._id === chat._id
                        ? "linear-gradient(135deg, #6e3aff, #00b3ff)"
                        : "var(--ch-item)"
                    }
                    color={
                      selectedChat?._id === chat._id
                        ? "#fff"
                        : "var(--ch-text)"
                    }
                    _hover={{
                      bg:
                        selectedChat?._id === chat._id
                          ? "linear-gradient(135deg, #6e3aff, #00b3ff)"
                          : "var(--ch-item-hover)",
                    }}
                    border="1px solid var(--ch-item-border)"
                    boxShadow={
                      selectedChat?._id === chat._id
                        ? "0 8px 24px rgba(110,58,255,0.45)"
                        : "none"
                    }
                    px={3}
                    py={2}
                    borderRadius="lg"
                    key={chat._id}
                  >
                    <Box display="flex" alignItems="center" gap={2}>
                      {!chat.isGroupChat && peer && (
                        <Box position="relative" flexShrink={0}>
                          <Avatar size="sm" name={peer.name} src={peer.pic} />
                          <Box
                            position="absolute"
                            bottom="0"
                            right="0"
                            w="11px"
                            h="11px"
                            borderRadius="full"
                            bg={isOnline ? "#22c55e" : "rgba(100,116,139,0.9)"}
                            border="2px solid #0a0f26"
                            title={isOnline ? "Online" : "Offline"}
                          />
                        </Box>
                      )}
                      {chat.isGroupChat && (
                        <GroupChatAvatar
                          users={chat.users}
                          name={chat.chatName}
                          pic={chat.pic}
                          size={32}
                          currentUserId={myId}
                        />
                      )}
                      <Box flex="1" minW={0}>
                        <Text
                          isTruncated
                          fontWeight={unreadLabel ? "700" : "400"}
                        >
                          {!chat.isGroupChat
                            ? getSender(loggedUser, chat.users)
                            : chat.chatName}
                        </Text>
                        {chat.latestMessage ? (
                          <Text fontSize="xs" color="var(--ch-preview)" isTruncated>
                            <b>
                              {chat.latestMessage.isDeleted
                                ? "Deleted"
                                : `${chat.latestMessage.sender?.name || "Unknown"} : `}
                            </b>
                            {(() => {
                              if (chat.latestMessage.isDeleted) return "This message was deleted";
                              const text = decodeStoredContent(
                                chat.latestMessage.content
                              );
                              if (typeof text !== "string") return text;
                              return text.length > 50
                                ? text.substring(0, 51) + "..."
                                : text;
                            })()}
                          </Text>
                        ) : (
                          <Text fontSize="xs" color="var(--ch-faint)">
                            No messages yet
                          </Text>
                        )}
                      </Box>
                      {chat.isMuted && (
                        <Text
                          flexShrink={0}
                          fontSize="sm"
                          title="Muted — no notifications"
                          aria-label="Muted"
                        >
                          🔇
                        </Text>
                      )}
                      {!chat.isGroupChat && chat.peerBlockedMe && (
                        <Text
                          flexShrink={0}
                          fontSize="sm"
                          title="This user blocked you"
                          aria-label="Blocked you"
                        >
                          🚫
                        </Text>
                      )}
                      {unreadLabel ? (
                        <Text
                          flexShrink={0}
                          minW="24px"
                          h="24px"
                          px="7px"
                          borderRadius="full"
                          display="inline-flex"
                          alignItems="center"
                          justifyContent="center"
                          fontSize="12px"
                          fontWeight="800"
                          color="#fff"
                          bg="linear-gradient(135deg, #6e3aff, #00c2ff)"
                          boxShadow="0 4px 14px rgba(110,58,255,0.55)"
                        >
                          {unreadLabel}
                        </Text>
                      ) : isMine ? (
                        <Text
                          flexShrink={0}
                          fontSize="sm"
                          fontWeight="800"
                          color={seen ? "var(--ch-seen)" : "var(--ch-unseen)"}
                          title={seen ? "Seen" : "Delivered"}
                        >
                          ✓✓
                        </Text>
                      ) : null}
                      {canDelete(chat) && (
                        <IconButton
                          aria-label="Delete chat"
                          icon={<DeleteIcon />}
                          size="xs"
                          variant="ghost"
                          color="var(--ch-danger)"
                          _hover={{ bg: "rgba(255,0,0,0.15)", color: "var(--ch-danger-text)" }}
                          onClick={(e) => handleDelete(e, chat)}
                          isLoading={String(deletingId) === String(chat._id)}
                        />
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Stack>
            {hasMore && (
              <Button
                size="sm"
                variant="ghost"
                color="#67e8f9"
                _hover={{ bg: "rgba(255,255,255,0.08)" }}
                onClick={() => fetchChats(page + 1, true)}
                isLoading={loadingMore}
                mt={2}
              >
                Load more chats
              </Button>
            )}
          </>
        ) : (
          <ChatLoading />
        )}
      </Box>
    </Box>
  );
};

export default MyChats;
